import { useLayoutEffect, useRef } from "react";
import { ArrowUpRight, Phone, MessageCircle } from "lucide-react";
import { SERVICES, PROCESS, GOOD_TO_KNOW, FAQ, CONTACT, NAV_LINKS } from "./content";

// ────────────────────────────────────────────────────────────────
// DESIGN 1 — EDITORIAL HOUSE  (Claridge's DNA, elevated)
// Cream · ink · brushed brass · Cormorant Garamond + Hanken Grotesk
// Full-bleed kitchen hero · restrained Anime.js v4 motion
// ────────────────────────────────────────────────────────────────

const CSS = `
.gk-root *,.gk-root *::before,.gk-root *::after{margin:0;padding:0;box-sizing:border-box}
.gk-root{
  --cream:#F4EEE2;--cream-deep:#EAE1CE;--ink:#16120B;--ink-soft:#3C3324;
  --brass:#AD8A4E;--brass-lt:#C8A567;--brass-deep:#8A6932;--line:rgba(22,18,11,.16);
  --serif:'Cormorant Garamond',Georgia,serif;--sans:'Hanken Grotesk',system-ui,sans-serif;
  font-family:var(--sans);background:var(--cream);color:var(--ink);overflow-x:hidden;
  -webkit-font-smoothing:antialiased;line-height:1.6;
}
.gk-root h1,.gk-root h2,.gk-root h3{font-family:var(--serif);font-weight:400;line-height:1.05}
.gk-eyebrow{font-size:13px;letter-spacing:2.5px;text-transform:uppercase;font-weight:600}
.gk-wrap{max-width:1180px;margin:0 auto;padding:0 56px}
.gk-section{padding:130px 0}

/* NAV */
.gk-nav{position:fixed;top:0;left:0;right:0;z-index:60;display:flex;justify-content:space-between;
  align-items:center;padding:30px 56px;transition:background .4s,padding .4s}
.gk-nav.scrolled{background:rgba(16,18,11,.96);padding:18px 56px;box-shadow:0 2px 40px rgba(0,0,0,.35)}
.gk-logo{font-family:var(--serif);font-size:30px;color:#fff;letter-spacing:.5px}
.gk-logo i{font-style:italic;color:var(--brass-lt)}
.gk-navlinks{display:flex;gap:36px;align-items:center}
.gk-navlinks a{color:rgba(255,255,255,.82);text-decoration:none;font-size:13px;letter-spacing:1.5px;
  text-transform:uppercase;font-weight:500;transition:color .3s}
.gk-navlinks a:hover{color:var(--brass-lt)}
.gk-navcta{border:1px solid rgba(255,255,255,.5);padding:9px 20px}
.gk-navcta:hover{background:var(--brass);border-color:var(--brass);color:#fff!important}
.gk-navphone{font-family:var(--serif);font-style:italic;font-size:21px;color:#fff;text-decoration:none}

/* HERO */
.gk-hero{position:relative;min-height:100vh;display:flex;align-items:flex-end;overflow:hidden}
.gk-hero-img{position:absolute;inset:0;background:url('/generated/hero_kitchen.png') center/cover no-repeat;
  transform:scale(1.12);animation:gk-kb 20s ease-out forwards}
@keyframes gk-kb{to{transform:scale(1)}}
.gk-scrim{position:absolute;inset:0;background:linear-gradient(180deg,rgba(15,12,7,.5) 0%,
  rgba(15,12,7,.14) 36%,rgba(15,12,7,.56) 76%,rgba(15,12,7,.84) 100%)}
.gk-hero-inner{position:relative;z-index:10;width:100%;max-width:1180px;margin:0 auto;padding:0 56px 60px}
.gk-hero .gk-eyebrow{color:var(--brass-lt);margin-bottom:26px;display:flex;align-items:center;gap:14px}
.gk-hero .gk-eyebrow::before{content:"";width:38px;height:1px;background:var(--brass-lt)}
.gk-h1{color:#fff;font-size:clamp(54px,8.2vw,124px);letter-spacing:-1.5px;margin-bottom:26px}
.gk-ln{display:block;overflow:hidden}
.gk-wi{display:inline-block;will-change:transform}
.gk-h1 em{font-style:italic;color:var(--brass-lt)}
.gk-rule{height:1px;background:var(--brass-lt);width:min(420px,58%);
  transform-origin:left;margin-bottom:30px}
.gk-sub{font-size:19px;line-height:1.7;color:rgba(255,255,255,.92);max-width:560px;
  margin-bottom:38px}
.gk-cta{display:flex;gap:16px;flex-wrap:wrap}
.gk-btn{display:inline-flex;align-items:center;gap:10px;padding:17px 36px;font-size:13px;
  letter-spacing:2px;text-transform:uppercase;font-weight:600;text-decoration:none;transition:all .35s;cursor:pointer}
.gk-btn-solid{background:var(--brass);color:#fff;border:1px solid var(--brass)}
.gk-btn-solid:hover{background:var(--brass-lt);border-color:var(--brass-lt);transform:translateY(-2px)}
.gk-btn-ghost{border:1px solid rgba(255,255,255,.55);color:#fff}
.gk-btn-ghost:hover{background:rgba(255,255,255,.1);border-color:#fff}
.gk-trust{display:flex;flex-wrap:wrap;margin-top:54px;border-top:1px solid rgba(255,255,255,.22);padding-top:24px}
.gk-trust span{font-size:13px;letter-spacing:1.5px;text-transform:uppercase;color:rgba(255,255,255,.92);
  font-weight:500;padding-right:34px;margin-right:34px;border-right:1px solid rgba(255,255,255,.22)}
.gk-trust span:last-child{border-right:none;margin-right:0;padding-right:0}
.gk-trust b{color:var(--brass-lt);font-weight:600}

/* SECTION HEADER */
.gk-head{max-width:720px;margin:0 auto 76px;text-align:center}
.gk-head .gk-eyebrow{color:var(--brass);margin-bottom:18px}
.gk-h2{font-size:clamp(36px,4.6vw,60px);letter-spacing:-1px;color:var(--ink);margin-bottom:22px}
.gk-h2 em{font-style:italic;color:var(--brass-deep)}
.gk-lead{font-size:18px;line-height:1.8;color:var(--ink-soft);max-width:560px;margin:0 auto}

/* PROCESS */
.gk-proc{background:var(--cream-deep)}
.gk-proc-grid{display:grid;grid-template-columns:repeat(3,1fr);max-width:1100px;margin:0 auto}
.gk-step{padding:48px 44px;text-align:center;border-left:1px solid var(--line)}
.gk-step:first-child{border-left:none}
.gk-step .n{font-family:var(--serif);font-style:italic;font-size:64px;color:var(--brass);margin-bottom:18px;line-height:1}
.gk-step h3{font-size:27px;color:var(--ink);margin-bottom:14px}
.gk-step p{font-size:15.5px;line-height:1.75;color:var(--ink-soft);max-width:280px;margin:0 auto}

/* SERVICES */
.gk-serv-top{display:grid;grid-template-columns:1fr 1.35fr;gap:80px;align-items:end;
  max-width:1180px;margin:0 auto 70px;padding:0 56px}
.gk-serv-top .gk-eyebrow{color:var(--brass);margin-bottom:18px}
.gk-serv-top h2{font-size:clamp(36px,4.6vw,60px);letter-spacing:-1px;color:var(--ink);line-height:1.02}
.gk-serv-top h2 em{font-style:italic;color:var(--brass-deep)}
.gk-serv-top p{font-size:19px;line-height:1.65;color:var(--ink-soft);font-style:italic;font-family:var(--serif)}
.gk-serv-list{max-width:1180px;margin:0 auto;padding:0 56px;border-top:1px solid var(--line)}
.gk-serv-row{display:grid;grid-template-columns:70px 40px 1.1fr 1.4fr 44px;gap:36px;align-items:center;
  padding:30px 0;border-bottom:1px solid var(--line);text-decoration:none;color:var(--ink);
  transition:background .35s,padding-left .35s}
.gk-serv-row:hover{background:rgba(173,138,78,.07);padding-left:16px}
.gk-serv-row .num{font-family:var(--serif);font-style:italic;font-size:22px;color:var(--brass)}
.gk-serv-row .nm{font-family:var(--serif);font-size:29px;color:var(--ink);letter-spacing:-.4px}
.gk-serv-row .ds{font-size:15px;line-height:1.6;color:var(--ink-soft)}

/* THE WORK (new imagery: mikveh + pots/pans) */
.gk-work{background:var(--ink);color:var(--cream)}
.gk-work .gk-head .gk-eyebrow{color:var(--brass-lt)}
.gk-work .gk-h2{color:#fff}
.gk-work .gk-lead{color:rgba(244,238,226,.7)}
.gk-work-grid{display:grid;grid-template-columns:1fr 1fr;gap:40px;max-width:1180px;margin:0 auto;padding:0 56px}
.gk-work-card{position:relative}
.gk-work-img{aspect-ratio:4/3;background:var(--cream-deep) center/cover no-repeat;position:relative}
.gk-work-img::after{content:"";position:absolute;inset:16px;border:1px solid rgba(200,165,103,.45);pointer-events:none}
.gk-work-cap{display:flex;align-items:baseline;gap:14px;margin-top:24px}
.gk-work-cap .k{font-family:var(--serif);font-style:italic;font-size:24px;color:var(--brass-lt)}
.gk-work-cap .v{font-size:14px;color:rgba(244,238,226,.7);letter-spacing:.3px}

/* ABOUT */
.gk-about{background:var(--ink);color:var(--cream);position:relative}
.gk-about-grid{display:grid;grid-template-columns:1fr 1.2fr;gap:100px;align-items:center;
  max-width:1180px;margin:0 auto;padding:0 56px}
.gk-about-photo{position:relative}
.gk-about-photo .ph{aspect-ratio:3/4;background:url('/generated/rabbi_hands.png') center/cover}
.gk-about-photo .fr{position:absolute;top:20px;left:20px;right:-20px;bottom:-20px;
  border:1px solid rgba(200,165,103,.5);z-index:-1}
.gk-about-photo .cred{position:absolute;bottom:-28px;right:-28px;background:var(--cream);color:var(--ink);
  padding:18px 24px}
.gk-about-photo .cred .t{font-size:11px;letter-spacing:2.5px;text-transform:uppercase;color:var(--brass-deep);font-weight:700}
.gk-about-photo .cred .n{font-family:var(--serif);font-style:italic;font-size:19px;margin-top:4px}
.gk-about-text .gk-eyebrow{color:var(--brass-lt);margin-bottom:20px}
.gk-about-text h2{font-size:clamp(38px,4.6vw,60px);letter-spacing:-1px;margin-bottom:30px;color:#fff}
.gk-about-text h2 em{font-style:italic;color:var(--brass-lt)}
.gk-about-text .q{font-family:var(--serif);font-style:italic;font-size:23px;
  color:rgba(244,238,226,.9);line-height:1.5;margin-bottom:28px}
.gk-about-text p{font-size:16px;line-height:1.85;color:rgba(244,238,226,.7);margin-bottom:18px;max-width:560px}
.gk-about-text a{display:inline-block;margin-top:14px;color:var(--brass-lt);text-decoration:none;
  font-size:12px;letter-spacing:3px;text-transform:uppercase;border-bottom:1px solid var(--brass-lt);padding-bottom:4px}

/* GOOD TO KNOW */
.gk-gtk{background:var(--cream-deep)}
.gk-gtk-grid{display:grid;grid-template-columns:1fr 1fr;gap:1px;background:var(--line);
  border:1px solid var(--line);max-width:1100px;margin:0 auto}
.gk-gtk-grid .c{background:var(--cream);padding:36px 38px}
.gk-gtk-grid .c .q{font-family:var(--serif);font-size:23px;color:var(--ink);margin-bottom:12px;line-height:1.25}
.gk-gtk-grid .c .a{font-size:15px;line-height:1.75;color:var(--ink-soft)}

/* FAQ */
.gk-faq-list{max-width:900px;margin:0 auto;border-top:1px solid var(--line)}
.gk-faq-list details{border-bottom:1px solid var(--line);padding:26px 0;cursor:pointer}
.gk-faq-list summary{font-family:var(--serif);font-size:23px;list-style:none;display:flex;
  justify-content:space-between;align-items:center;color:var(--ink);gap:16px}
.gk-faq-list summary::-webkit-details-marker{display:none}
.gk-faq-list summary .p{color:var(--brass);font-size:26px;font-weight:300}
.gk-faq-list p{margin-top:16px;font-size:15.5px;line-height:1.8;color:var(--ink-soft);max-width:720px}

/* CONTACT */
.gk-contact{background:var(--ink);color:var(--cream);text-align:center}
.gk-contact .gk-eyebrow{color:var(--brass-lt);margin-bottom:22px}
.gk-contact h2{font-size:clamp(44px,6vw,86px);letter-spacing:-2px;color:#fff;line-height:1;margin-bottom:28px}
.gk-contact h2 em{font-style:italic;color:var(--brass-lt)}
.gk-contact .s{font-family:var(--serif);font-style:italic;font-size:22px;
  color:rgba(244,238,226,.65);margin-bottom:50px}
.gk-contact .row{display:inline-flex;gap:16px;flex-wrap:wrap;justify-content:center;margin-bottom:48px}
.gk-contact .em{font-size:14px;color:rgba(244,238,226,.6)}
.gk-contact .em a{color:var(--brass-lt);text-decoration:none;font-family:var(--serif);
  font-style:italic;font-size:19px}

/* FOOTER */
.gk-footer{background:var(--ink);color:rgba(244,238,226,.42);padding:34px 56px;
  border-top:1px solid rgba(244,238,226,.08);display:flex;justify-content:space-between;
  flex-wrap:wrap;gap:16px;font-size:12px;letter-spacing:2px;text-transform:uppercase}

/* WHATSAPP STICKY */
.gk-wa{position:fixed;bottom:28px;right:28px;z-index:70;width:58px;height:58px;border-radius:50%;
  background:#25D366;color:#fff;display:flex;align-items:center;justify-content:center;
  text-decoration:none;box-shadow:0 6px 24px rgba(37,211,102,.45);transition:transform .3s}
.gk-wa:hover{transform:scale(1.08)}

/* REVEAL — hidden ONLY while JS is running (no-JS / crawlers / failed-JS see full content) */
.gk-root.gk-js .gk-hero .gk-eyebrow,
.gk-root.gk-js #gk-sub,
.gk-root.gk-js #gk-cta,
.gk-root.gk-js #gk-trust,
.gk-root.gk-js .gk-reveal{opacity:0}
.gk-root.gk-js #gk-rule{transform:scaleX(0)}
.gk-root.gk-js .gk-h1 .gk-wi{transform:translateY(110%)}

@media(max-width:980px){
  .gk-wrap,.gk-serv-top,.gk-serv-list,.gk-work-grid,.gk-about-grid{padding-left:32px;padding-right:32px}
  .gk-serv-top,.gk-about-grid{grid-template-columns:1fr;gap:44px}
  .gk-proc-grid{grid-template-columns:1fr}
  .gk-step{border-left:none;border-top:1px solid var(--line)}
  .gk-step:first-child{border-top:none}
  .gk-work-grid,.gk-gtk-grid{grid-template-columns:1fr}
  .gk-about-photo{max-width:360px}
}
@media(max-width:640px){
  .gk-nav{padding:20px 22px}.gk-nav.scrolled{padding:14px 22px}
  .gk-navlinks{display:none}
  .gk-section{padding:80px 0}
  .gk-wrap,.gk-hero-inner,.gk-serv-top,.gk-serv-list,.gk-work-grid,.gk-about-grid{padding-left:22px;padding-right:22px}
  .gk-hero-inner{padding-bottom:44px}
  .gk-sub{font-size:17px}
  .gk-trust span{border-right:none;margin-right:0;padding-right:0;width:100%;padding-bottom:8px}
  .gk-serv-row{grid-template-columns:48px 1fr;gap:14px}
  .gk-serv-row .gk-ico,.gk-serv-row .ds,.gk-serv-row .ar{display:none}
  .gk-footer{flex-direction:column;gap:8px}
}
@media(prefers-reduced-motion:reduce){
  .gk-hero-img{animation:none;transform:none}
}
`;

export default function DesignOne() {
  const root = useRef(null);

  useLayoutEffect(() => {
    const el = root.current;
    if (!el) return;
    el.classList.add("gk-js"); // hide animated elements ONLY now that JS is live
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Split hero headline into per-word spans (safe DOM, no innerHTML)
    el.querySelectorAll(".gk-h1 [data-split]").forEach((line) => {
      const words = line.textContent.trim().split(/\s+/);
      while (line.firstChild) line.removeChild(line.firstChild);
      words.forEach((w, i) => {
        const s = document.createElement("span");
        s.className = "gk-wi";
        s.textContent = w;
        line.appendChild(s);
        if (i < words.length - 1) line.appendChild(document.createTextNode(" "));
      });
    });

    // Nav scrolled state
    const nav = el.querySelector(".gk-nav");
    const onScroll = () => nav && nav.classList.toggle("scrolled", window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const reveals = el.querySelectorAll("[data-reveal]");

    const showAll = () => {
      el.querySelectorAll(".gk-wi").forEach((w) => (w.style.transform = "none"));
      ["#gk-eb", "#gk-sub", "#gk-cta", "#gk-trust"].forEach((s) => {
        const n = el.querySelector(s);
        if (n) n.style.opacity = 1;
      });
      const r = el.querySelector("#gk-rule");
      if (r) r.style.transform = "scaleX(1)";
      reveals.forEach((n) => (n.style.opacity = 1));
    };

    let cleanup = () => {};
    if (reduce) {
      showAll();
    } else {
      import("animejs")
        .then(({ animate, stagger, createTimeline }) => {
          createTimeline({ defaults: { ease: "expo.out" } })
            .add("#gk-eb", { opacity: [0, 1], y: [14, 0], duration: 900 }, 250)
            .add(".gk-h1 .gk-wi", { y: ["110%", "0%"], duration: 1150, delay: stagger(70) }, 400)
            .add("#gk-rule", { scaleX: [0, 1], duration: 900, ease: "inOutQuint" }, 1050)
            .add("#gk-sub", { opacity: [0, 1], y: [18, 0], duration: 900 }, 1150)
            .add("#gk-cta", { opacity: [0, 1], y: [18, 0], duration: 900 }, 1300)
            .add("#gk-trust span", { opacity: [0, 1], y: [12, 0], duration: 800, delay: stagger(90) }, 1450);

          const io = new IntersectionObserver(
            (ents) => {
              ents.forEach((en) => {
                if (en.isIntersecting) {
                  animate(en.target, { opacity: [0, 1], y: [28, 0], duration: 1100, ease: "expo.out" });
                  io.unobserve(en.target);
                }
              });
            },
            { threshold: 0.16 }
          );
          reveals.forEach((n) => io.observe(n));
          cleanup = () => io.disconnect();
        })
        .catch(showAll); // animejs unavailable → no motion, full content
    }

    return () => {
      window.removeEventListener("scroll", onScroll);
      cleanup();
    };
  }, []);

  return (
    <div className="gk-root" ref={root} data-testid="design-1">
      <style>{CSS}</style>

      {/* NAV */}
      <nav className="gk-nav">
        <div className="gk-logo">Go<i>Kosher</i></div>
        <div className="gk-navlinks">
          {NAV_LINKS.map((l) => (
            <a key={l.label} href={l.href} className={l.label === "Contact" ? "gk-navcta" : ""}
              data-testid={`nav-${l.label.toLowerCase().replace(/ /g, "-")}`}>
              {l.label}
            </a>
          ))}
        </div>
        <a className="gk-navphone" href={CONTACT.phoneRaw}>{CONTACT.phone}</a>
      </nav>

      {/* HERO */}
      <header className="gk-hero">
        <div className="gk-hero-img" />
        <div className="gk-scrim" />
        <div className="gk-hero-inner">
          <div className="gk-eyebrow" id="gk-eb">Established · London Kashrus</div>
          <h1 className="gk-h1">
            <span className="gk-ln" data-split>Koshering</span>
            <span className="gk-ln" data-split><em>your kitchen.</em></span>
          </h1>
          <div className="gk-rule" id="gk-rule" />
          <p className="gk-sub" id="gk-sub">
            Bespoke kashrus for the homes and businesses of London — carried out quietly,
            properly, and to your minhag. Same-day service, when you need it.
          </p>
          <div className="gk-cta" id="gk-cta">
            <a className="gk-btn gk-btn-solid" href="#contact">Book Now <ArrowUpRight size={15} /></a>
            <a className="gk-btn gk-btn-ghost" href={CONTACT.phoneRaw}><Phone size={14} /> {CONTACT.phone}</a>
          </div>
          <div className="gk-trust" id="gk-trust">
            <span><b>Same Day Service</b> · 24 Hours Available</span>
            <span>Smicha · <b>Chief Rabbi of Israel</b></span>
            <span>Bespoke · Every Minhag</span>
            <span><b>All Around London</b> · UK-wide</span>
          </div>
        </div>
      </header>

      {/* PROCESS */}
      <section id="process" className="gk-section gk-proc">
        <div className="gk-head">
          <div className="gk-eyebrow gk-reveal" data-reveal>Our Process</div>
          <h2 className="gk-h2 gk-reveal" data-reveal>Three steps, <em>nothing more.</em></h2>
        </div>
        <div className="gk-proc-grid">
          {PROCESS.map((p) => (
            <div className="gk-step gk-reveal" data-reveal key={p.num}>
              <div className="n">{p.num}</div>
              <h3>{p.title}</h3>
              <p>{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="gk-section">
        <div className="gk-serv-top">
          <div>
            <div className="gk-eyebrow gk-reveal" data-reveal>Services</div>
            <h2 className="gk-reveal" data-reveal>Eight disciplines,<br /><em>one standard.</em></h2>
          </div>
          <p className="gk-reveal" data-reveal>
            From a single kitchen to a full commercial fit-out — bespoke service for every
            tradition, observed in every detail.
          </p>
        </div>
        <div className="gk-serv-list">
          {SERVICES.map((s) => {
            const Icon = s.icon;
            return (
              <a key={s.num} href="#contact" className="gk-serv-row gk-reveal" data-reveal
                data-testid={`service-${s.num}`}>
                <div className="num">{s.num}</div>
                <div className="gk-ico"><Icon size={22} strokeWidth={1.3} color="var(--brass)" /></div>
                <div className="nm">{s.name}</div>
                <div className="ds">{s.desc}</div>
                <ArrowUpRight className="ar" size={22} color="var(--brass)" strokeWidth={1.2} />
              </a>
            );
          })}
        </div>
      </section>

      {/* THE WORK — mikveh + pots/pans imagery */}
      <section className="gk-section gk-work">
        <div className="gk-head">
          <div className="gk-eyebrow gk-reveal" data-reveal>Carried Out Properly</div>
          <h2 className="gk-h2 gk-reveal" data-reveal>Every utensil, <em>honoured.</em></h2>
          <p className="gk-lead gk-reveal" data-reveal>
            From the mikveh to the flame — the work is done with the care it deserves.
          </p>
        </div>
        <div className="gk-work-grid">
          <div className="gk-work-card gk-reveal" data-reveal>
            <div className="gk-work-img" style={{ backgroundImage: "url('/generated/mikveh.png')" }} />
            <div className="gk-work-cap"><span className="k">Tovling Keilim</span>
              <span className="v">Immersion at a kosher mikveh — collection &amp; return.</span></div>
          </div>
          <div className="gk-work-card gk-reveal" data-reveal>
            <div className="gk-work-img" style={{ backgroundImage: "url('/generated/pots_pans.png')" }} />
            <div className="gk-work-cap"><span className="k">Hagalah</span>
              <span className="v">Pots, pans &amp; cutlery kashered and returned same day.</span></div>
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="gk-section gk-about">
        <div className="gk-about-grid">
          <div className="gk-about-photo gk-reveal" data-reveal>
            <div className="ph" />
            <div className="fr" />
            <div className="cred">
              <div className="t">Smicha</div>
              <div className="n">HaRav Mordechai Eliyahu z"l</div>
            </div>
          </div>
          <div className="gk-about-text">
            <div className="gk-eyebrow gk-reveal" data-reveal>About the Rabbi</div>
            <h2 className="gk-reveal" data-reveal>Rabbi Hillel <em>Gruber.</em></h2>
            <p className="q gk-reveal" data-reveal>
              "What I bring to a kitchen is not haste, but quiet. The work is older than any
              of us — my task is to honour it."
            </p>
            <p className="gk-reveal" data-reveal>
              Smicha from HaRav Mordechai Eliyahu z"l, Chief Rabbi of the State of Israel —
              one of the most respected rabbinic authorities of the modern era. Twenty years
              of hands-on kashrus practice.
            </p>
            <p className="gk-reveal" data-reveal>
              Former Kashrut Director of FoodStory.co.uk and founder of the Talmud Club in
              Central London. Based all around London, serving homes and businesses across
              the UK — every tradition, Ashkenazi, Sephardi and Chabad, to your minhag.
            </p>
            <a className="gk-reveal" data-reveal href="#contact">Speak with the Rabbi →</a>
          </div>
        </div>
      </section>

      {/* GOOD TO KNOW */}
      <section id="good-to-know" className="gk-section gk-gtk">
        <div className="gk-head">
          <div className="gk-eyebrow gk-reveal" data-reveal>Good to Know</div>
          <h2 className="gk-h2 gk-reveal" data-reveal>The little things <em>that matter.</em></h2>
          <p className="gk-lead gk-reveal" data-reveal>
            The questions we hear most — answered straight, before you even ask.
          </p>
        </div>
        <div className="gk-gtk-grid">
          {GOOD_TO_KNOW.map((g, i) => (
            <div className="c gk-reveal" data-reveal key={i}>
              <div className="q">{g.q}</div>
              <div className="a">{g.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="gk-section">
        <div className="gk-head">
          <div className="gk-eyebrow gk-reveal" data-reveal>Frequently Asked</div>
          <h2 className="gk-h2 gk-reveal" data-reveal>Questions, <em>answered.</em></h2>
        </div>
        <div className="gk-faq-list">
          {FAQ.map((f, i) => (
            <details key={i} className="gk-reveal" data-reveal>
              <summary>{f.q}<span className="p">+</span></summary>
              <p>{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="gk-section gk-contact">
        <div className="gk-wrap">
          <div className="gk-eyebrow gk-reveal" data-reveal>Get in Touch</div>
          <h2 className="gk-reveal" data-reveal>One call. <em>One kitchen.</em><br />Done properly.</h2>
          <p className="s gk-reveal" data-reveal>Telephone, message, or write — we respond, always.</p>
          <div className="row gk-reveal" data-reveal>
            <a className="gk-btn gk-btn-solid" href={CONTACT.phoneRaw}><Phone size={14} /> {CONTACT.phone}</a>
            <a className="gk-btn gk-btn-ghost" href={CONTACT.whatsapp} target="_blank" rel="noreferrer">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>
          <div className="em gk-reveal" data-reveal>
            or write to <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
          </div>
        </div>
      </section>

      <footer className="gk-footer">
        <span>GoKosher · MMXXVI</span>
        <span>Rabbi Hillel Gruber</span>
        <span>London · United Kingdom</span>
      </footer>

      <a className="gk-wa" href={CONTACT.whatsapp} target="_blank" rel="noreferrer" aria-label="WhatsApp GoKosher">
        <MessageCircle size={26} />
      </a>
    </div>
  );
}
